import React, { useEffect, useState, forwardRef, useMemo } from 'react';
import type { ReviewCardProps } from './ReviewCard';
import ReviewCard from './ReviewCard';
import dayjs from 'dayjs';
import type { KeenSliderOptions } from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';
import ArrowCircle from '@components/Carousel/components/ArrowCircle';
import useWindowSize from '@customHooks/useWindowSize';
import { useQuery } from '@tanstack/react-query';
import { indexReviewsQueryOptions } from '@queries/index';
import './styles.scss';
import { twcls } from '@utils/twcls';

interface ReviewsAboutUsProps {
  reviewsProps?: ReviewCardProps[];
  titleClassName?: string;
}

const ReviewsAboutUs = forwardRef<HTMLElement, ReviewsAboutUsProps>(
  ({ reviewsProps = [], titleClassName = '' }, ref) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const { isMobile } = useWindowSize();

    // Тот же кэш, что и блок «Отзывы о нас» на главной, но свой select:
    // маппинг в ReviewCardProps и без обрезки до 6 элементов
    const { data: fetchedReviews = [] } = useQuery({
      ...indexReviewsQueryOptions(20),
      select: res =>
        (res.getIndexReviews?.reviews ?? [])
          .map(item => ({
            id: item.id,
            fio: item.fio || '',
            rating: item.rating || 5,
            date: item.date || '',
            text: item.text || '',
            peopleItem: item?.peopleItem
          }))
          .filter(
            item => item.text && item.text.length > 0 && item.text !== ' '
          ),
      enabled: !reviewsProps?.length
    });

    const reviews: ReviewCardProps[] = reviewsProps?.length
      ? reviewsProps
      : fetchedReviews;

    const options = useMemo<KeenSliderOptions>(
      () => ({
        rubberband: false,
        loop: true,
        mode: 'free-snap' as const,
        slides: {
          perView: 'auto' as const,
          spacing: 20
        },
        breakpoints: {
          '(min-width: 1280px)': {
            loop: true,
            slides: {
              perView: 3,
              spacing: 20
            }
          }
        },
        slideChanged(slider) {
          setCurrentSlide(slider.track.details.rel);
        },
        created() {
          setLoaded(true);
        }
      }),
      []
    );

    const [sliderRef, instanceRef] = useKeenSlider(options);

    useEffect(() => {
      if (instanceRef?.current) {
        instanceRef.current?.update(options);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviews, reviewsProps]);

    if (!reviews?.length) {
      return null;
    }
    return (
      <section ref={ref} id='reviews' className='reviews-about-us'>
        <div className='reviews-about-us__container'>
          <div className='reviews-about-us__header'>
            <h2
              className={twcls(
                'reviews-about-us__title oysterbar-test-title-secondary',
                titleClassName
              )}
            >
              Отзывы о нас
            </h2>
          </div>

          <div className='reviews-about-us__slider-container'>
            {!isMobile && loaded && instanceRef.current && (
              <div className='arrow-circle-container'>
                <ArrowCircle
                  left
                  onClick={e => {
                    e.stopPropagation();
                    instanceRef.current?.prev();
                  }}
                />
                <ArrowCircle
                  onClick={e => {
                    e.stopPropagation();
                    instanceRef.current?.next();
                  }}
                  disabled={
                    currentSlide ===
                      instanceRef.current.track.details.slides.length - 1 &&
                    !instanceRef.current.options.loop
                  }
                />
              </div>
            )}
            <div
              ref={sliderRef}
              className='keen-slider reviews-about-us__slider'
            >
              {reviews?.map(item => (
                <div key={item.id} className='keen-slider__slide'>
                  <ReviewCard
                    id={item.id}
                    date={dayjs(item?.date).format('DD MMMM YYYY')}
                    fio={item.fio}
                    peopleItem={item.peopleItem}
                    rating={item.rating}
                    text={item.text}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }
);

export default ReviewsAboutUs;
